import React, { useState, useRef, useEffect } from "react";
import { FiFilter, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { RiCheckFill } from "react-icons/ri";
import {
  FilterWrapper,
  FilterButton,
  DropdownContainer,
  FilterSection,
  SectionTitle,
  RadioOption,
  CheckboxOption,
  Footer,
  ColumnsContainer,
  FilterColumn
} from "./styles";
 
import { PrimaryButton } from "../PrimaryButton";
import { SecondaryButton } from "../SecondaryButton";
 
export interface FilterOption {
  label: React.ReactNode;
  value: string;
}
 
export interface FilterGroup {
  id: string;
  title: string;
  options: FilterOption[];
}
 
interface DropdownFilterProps {
  groups: FilterGroup[];
  onApply: (selections: Record<string, any>) => void;
  defaultValues?: Record<string, any>;
  multiple?: boolean;
  layout?: "default" | "orders";
}
 
export function DropdownFilter({ groups, onApply, defaultValues = {}, multiple = false, layout = "default" }: DropdownFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selections, setSelections] = useState<Record<string, any>>(defaultValues);
  const wrapperRef = useRef<HTMLDivElement>(null);
 
  // Initialize defaults
  useEffect(() => {
    if (Object.keys(selections).length === 0 && Object.keys(defaultValues).length > 0) {
      setSelections(defaultValues);
    }
  }, [defaultValues]);
 
  // Click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
 
  const handleSelect = (groupId: string, value: string) => {
    if (multiple) {
      setSelections(prev => {
        const currentGroupSelections = (prev[groupId] as string[]) || [];
        const isSelected = currentGroupSelections.includes(value);
        
        let newSelections: string[];
        if (isSelected) {
          newSelections = currentGroupSelections.filter(v => v !== value);
        } else {
          newSelections = [...currentGroupSelections, value];
        }
        
        return {
          ...prev,
          [groupId]: newSelections
        };
      });
    } else {
      setSelections(prev => ({
        ...prev,
        [groupId]: value
      }));
    }
  };

  const renderGroup = (group: FilterGroup) => {
    return (
      <FilterSection key={group.id}>
        {group.title && <SectionTitle>{group.title}</SectionTitle>}
        <div className="options-list">
          {group.options.map(option => {
            const isChecked = multiple
              ? ((selections[group.id] as string[]) || []).includes(option.value)
              : selections[group.id] === option.value;

            return multiple ? (
              <CheckboxOption key={option.value}>
                <input
                  type="checkbox"
                  name={group.id}
                  checked={isChecked}
                  onChange={() => handleSelect(group.id, option.value)}
                />
                <span className="checkbox-custom">
                  <RiCheckFill size={14} />
                </span>
                <span className="label">{option.label}</span>
              </CheckboxOption>
            ) : (
              <RadioOption key={option.value}>
                <input
                  type="radio"
                  name={group.id}
                  checked={isChecked}
                  onChange={() => handleSelect(group.id, option.value)}
                />
                <span className="radio-custom"></span>
                <span className="label">{option.label}</span>
              </RadioOption>
            );
          })}
        </div>
      </FilterSection>
    );
  };

  const handleClear = () => {
    const cleared: Record<string, any> = {};
    groups.forEach(group => {
      cleared[group.id] = multiple ? [] : "";
    });
    setSelections(cleared);
    onApply(cleared);
    setIsOpen(false);
  };
 
  const handleApply = () => {
    onApply(selections);
    setIsOpen(false);
  };
 
  return (
    <FilterWrapper ref={wrapperRef} className={isOpen ? "open" : ""}>
      <FilterButton onClick={() => setIsOpen(!isOpen)} active={isOpen}>
        <FiFilter size={16} />
        <span>Filtros</span>
        {isOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
      </FilterButton>
 
      {isOpen && (
        <DropdownContainer layout={layout}>
          <div className="groups-container">
            {layout === "orders" ? (
              <ColumnsContainer>
                <FilterColumn>
                  {["solution_type", "status"]
                    .map(id => groups.find(g => g.id === id))
                    .filter((g): g is FilterGroup => !!g)
                    .map(renderGroup)}
                </FilterColumn>
                <FilterColumn>
                  {["urgency", "billing_method"]
                    .map(id => groups.find(g => g.id === id))
                    .filter((g): g is FilterGroup => !!g)
                    .map(renderGroup)}
                </FilterColumn>
              </ColumnsContainer>
            ) : (
              groups.map(renderGroup)
            )}
          </div>
 
          <Footer>
            <SecondaryButton text="Limpar" onClick={handleClear} style={{ height: "36px", padding: "8px 16px" }} />
            <PrimaryButton text="Aplicar" onClick={handleApply} style={{ height: "36px", padding: "8px 24px" }} />
          </Footer>
        </DropdownContainer>
      )}
    </FilterWrapper>
  );
}